import { CrewClaim } from '~/app/features/crew/crewClaimMiddleware';
import { CrewOrder } from '~/app/features/order-middleware/orderMiddleware';
import { Settings } from '~/app/settingsMiddleware';
import { isCreditClaim } from '~/utils/claimLineItem';
import { ResolutionMethod } from '~/utils/constants';
import { CreditResolutionMethod } from '~/utils/schemas';

type CrewContext = {
  order: CrewOrder;
  settings: Settings;
  crewClaim: CrewClaim;
};

const defaultGiftCardTextContent =
  'Receive a gift card once your return is approved.';
const defaultRefundTextContent =
  'Receive a refund to your original payment method once your return is approved. Fees may apply.';

export const getDefaultCreditMethod = (
  offeredMethods: ReturnType<typeof getOfferedCreditMethods>,
  creditMethod?: CreditResolutionMethod,
) => {
  const creditMethodOption = offeredMethods.find(
    (method) => method.type === creditMethod,
  );
  if (creditMethodOption) return creditMethodOption;

  const giftCardMethod = offeredMethods.find(
    (method) => method.type === 'Gift_Card',
  );
  if (giftCardMethod) return giftCardMethod;

  const refundMethod = offeredMethods.find(
    (method) => method.type === 'Refund',
  );
  if (refundMethod) return refundMethod;

  return null;
};

type CreditMethod = {
  type: CreditResolutionMethod;
  label: string;
  description: string;
  badge?: string;
};

export const getOfferedCreditMethods = (
  settings: NonNullable<CrewContext['settings']>,
  lineItems: CrewContext['crewClaim']['claimLineItems'],
): CreditMethod[] => {
  // the credit method is claim wide, so one restricted line item omits it for the whole claim
  const isOmitted = (method: CreditResolutionMethod) =>
    lineItems
      .filter(isCreditClaim)
      .some((li) => li.omittedResolutionMethods?.includes(method));

  // ---- Refund -----
  const { isRefundEnabled } = settings.return;

  const isRefundOffered =
    isRefundEnabled &&
    !isOmitted(ResolutionMethod.refund) &&
    lineItems.some((li) => li.lineItem.return.refund.isEligible);

  // ---- Gift Card -----
  const { isGiftCardEnabled } = settings.return;

  const isGiftCardOffered =
    isGiftCardEnabled &&
    !isOmitted(ResolutionMethod.giftCard) &&
    lineItems.some((li) => li.lineItem.return.giftCard.isEligible);

  return [
    isGiftCardOffered
      ? {
          type: 'Gift_Card' as const,
          label: 'Gift Card',
          description:
            settings.theme.offerGiftCardDetailText ??
            defaultGiftCardTextContent,
        }
      : null,
    isRefundOffered
      ? {
          type: 'Refund' as const,
          label: 'Refund',
          description:
            settings.theme.offerRefundDetailText ?? defaultRefundTextContent,
        }
      : null,
  ].filter(Boolean);
};

export function getCreditMethods(
  settings: NonNullable<CrewContext['settings']>,
  claimLineItems: CrewContext['crewClaim']['claimLineItems'],
  creditMethod?: CreditResolutionMethod,
) {
  const offered = getOfferedCreditMethods(settings, claimLineItems);

  return {
    offered,
    selected: getDefaultCreditMethod(offered, creditMethod),
  };
}
